import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useState, useRef } from 'react';
import { useConfirmDialog } from 'src/hooks/useConfirmDialog';
import { useMatchActions } from 'src/hooks/useMatchActions';
import { MatchHeader } from './MatchHeader';
import { MatchWinnerSelector } from './MatchWinnerSelector';
import { MatchControlButtons } from './MatchControlButtons';
import { MatchAggregated } from 'functions/src/types/firestore/Game/Tournament/Bracket';
import { MatchScorer } from './MatchScorer';
import { useMatch } from '../tournaments/match/MatchProvider';
import { MatchDetails } from '../tournaments/match/details/MatchDetails';

export type MatchDrawerMatch = Pick<MatchAggregated<Date>, 'id' | 'startTime'> &
  Required<Pick<MatchAggregated<Date>, 'team1' | 'team2'>>;

export type MatchDrawerProps = {
  gameId: string;
  tournamentId: string;
  onClose: () => void;
};

export const MatchDrawer = ({
  onClose,
  gameId,
  tournamentId,
}: MatchDrawerProps) => {
  const { team1, team2, id: matchId, startTime } = useMatch();
  const team1Name = team1?.name;
  const team2Name = team2?.name;
  const [value, setValue] = useState('');
  const [hasScore, setHasScore] = useState(false);
  const team1Ref = useRef<HTMLInputElement>(null);
  const team2Ref = useRef<HTMLInputElement>(null);

  const { declareWinner, delayMatch, cancelMatch } = useMatchActions({
    gameId,
    matchId,
    tournamentId,
  });

  const { open: openConfirmDialog, close: closeConfirmDialog } =
    useConfirmDialog(matchId);

  const handleConfirm = (action: 'winner' | 'delay' | 'cancel') => {
    switch (action) {
      case 'winner':
        return openConfirmDialog({
          title: 'Confirming winner',
          description: `Are you sure you want to set ${value} as the winner?`,
          confirmFunction: async () => {
            await declareWinner(
              team1Name === value
                ? !!team1?.id
                  ? team1.id
                  : ''
                : !!team2?.id
                ? team2.id
                : '',
              team1Ref.current?.value
                ? Number(team1Ref.current?.value)
                : undefined,
              team2Ref.current?.value
                ? Number(team2Ref.current?.value)
                : undefined,
            );
            closeConfirmDialog();
            onClose();
          },
        });
      case 'delay':
        return openConfirmDialog({
          title: 'Confirming delay',
          description: `Are you sure you want to delay the match between ${team1Name} and ${team2Name}?`,
          confirmFunction: async () => {
            await delayMatch();
          },
        });
      case 'cancel':
        return openConfirmDialog({
          title: 'Confirming cancellation',
          description: `Are you sure you want to cancel the match between ${team1Name} and ${team2Name}?`,
          confirmFunction: async () => {
            await cancelMatch();
          },
        });
      default:
    }
  };

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleSwtichChange = (e) => {
    setHasScore(e.target.checked);
  };

  return (
    <Stack sx={{ mt: 16 }} spacing={4}>
      {team1Name && team2Name && (
        <MatchHeader team1Name={team1Name} team2Name={team2Name} />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <MatchDetails
          id={matchId}
          startTime={startTime}
          sx={{ flexDirection: 'column' }}
        />
      </Box>
      <MatchWinnerSelector
        team1Name={team1Name}
        team2Name={team2Name}
        value={value}
        handleChange={handleChange}
        handleConfirm={handleConfirm}
      />
      <MatchScorer
        team1Name={team1Name}
        team2Name={team2Name}
        hasScore={hasScore}
        handleSwtichChange={handleSwtichChange}
        team1Ref={team1Ref}
        team2Ref={team2Ref}
      />
      <MatchControlButtons handleConfirm={handleConfirm} />
    </Stack>
  );
};
